
<div
	data-qa-multiselect
	:tabindex="searchable ? -1 : tabindex"
	:class="{
		'multiselect--active': isOpen,
		'multiselect--disabled': disabled,
		'multiselect--above': isAbove
	}"
	:state="state"
	class="multiselect"
	role="combobox"
	:aria-owns="'listbox-' + id"
	@focus="activate()"
	@blur="searchable ? false : deactivate()"
	@keydown.self.down.prevent="pointerForward()"
	@keydown.self.up.prevent="pointerBackward()"
	@keypress.enter.tab.stop.self="addPointerElement($event)"
	@keyup.esc="deactivate()"
>
	<slot name="caret" :toggle="toggle">
		<div
			data-qa-multiselect-caret
			class="multiselect__select"
			@mousedown.prevent.stop="toggle()"
		/>
	</slot>
	<slot name="clear" :search="search" />
	<!-- type is used from flow (i.e. custom prop) -->
	<div ref="tags" class="multiselect__tags" :type="type">
		<slot
			name="selection"
			:search="search"
			:remove="removeElement"
			:values="visibleValues"
			:is-open="isOpen"
		>
			<div
				v-show="visibleValues.length > 0"
				v-if="!maxVisibleOptions && maxVisibleOptions !== 0"
				class="multiselect__tags-wrap"
			>
				<template v-for="(option, index) of visibleValues">
					<slot
						name="tag"
						:option="option"
						:search="search"
						:remove="removeElement"
						@mousedown.prevent
					>
						<span :key="index" class="multiselect__tag">
							<span v-text="getOptionLabel(option.title || option)" />
							<i
								tabindex="1"
								class="multiselect__tag-icon"
								@keypress.enter.prevent="removeElement(option)"
								@mousedown.prevent="removeElement(option)"
							/>
						</span>
					</slot>
				</template>
			</div>
			<div
				v-show="visibleValues.length > 0"
				v-else-if="maxVisibleOptions === 0"
				class="multiselect__tags-wrap"
			>
				<span></span>
			</div>
			<div v-show="visibleValues.length > 0" v-else class="multiselect__tags-wrap">
				<template v-for="(option, index) of visibleValues.slice(0, Number(maxVisibleOptions))">
					<slot
						name="tag"
						:option="option"
						:search="search"
						:remove="removeElement"
						@mousedown.prevent
					>
						<span :key="index" class="multiselect__tag">
							<span v-text="getOptionLabel(option.title || option)" />
							<i
								tabindex="1"
								class="multiselect__tag-icon"
								@keypress.enter.prevent="removeElement(option)"
								@mousedown.prevent="removeElement(option)"
							/>
						</span>
					</slot>
				</template>
			</div>
			<template v-if="internalValue && internalValue.length > limit">
				<slot name="limit">
					<strong class="multiselect__strong" v-text="limitText(internalValue.length - limit)" />
				</slot>
			</template>
		</slot>
		<transition name="multiselect__loading">
			<slot name="loading">
				<div v-show="loading" class="multiselect__spinner" />
			</slot>
		</transition>
		<input
			v-if="searchable"
			:id="id"
			ref="search"
			data-qa-multiselect-input
			:name="name"
			type="text"
			autocomplete="off"
			spellcheck="false"
			:placeholder="placeholder"
			:style="inputStyle"
			:value="search"
			:disabled="disabled"
			:tabindex="tabindex"
			class="multiselect__input"
			:aria-controls="'listbox-' + id"
			@input="updateSearch($event.target.value)"
			@focus.prevent="activate()"
			@blur.prevent="deactivate()"
			@keyup.esc="deactivate()"
			@keydown.down.prevent="pointerForward()"
			@keydown.up.prevent="pointerBackward()"
			@keypress.enter.prevent.stop.self="addPointerElement($event)"
			@keydown.delete.stop="removeLastElement()"
		/>
		<span v-if="isSingleLabelVisible" class="multiselect__single" @mousedown.prevent="toggle">
			<slot name="singleLabel" :option="singleValue">
				<template>{{ currentOptionLabel }}</template>
			</slot>
		</span>
		<span
			v-if="isPlaceholderVisible"
			class="multiselect__placeholder"
			@mousedown.prevent="toggle"
		>
			<slot name="placeholder">
				{{ placeholder }}
			</slot>
		</span>
	</div>
	<transition name="multiselect">
		<div
			v-show="isOpen"
			ref="list"
			class="multiselect__content-wrapper flow-add-scrollbar"
			tabindex="-1"
			:style="{
				maxHeight: optimizedHeight + 'px',
				width: optionsWidth,
				top: optionsTop
			}"
			@focus="activate"
			@mousedown.prevent
		>
			<ul :id="'listbox-' + id" class="multiselect__content" :style="contentStyle" role="listbox">
				<slot name="beforeList" />
				<li v-if="multiple && max === internalValue.length">
					<span class="multiselect__option">
						<slot name="maxElements">
							Maximum of {{ max }} options selected. First remove a selected option to select
							another.
						</slot>
					</span>
				</li>
				<template v-if="!max || internalValue.length < max">
					<li
						v-for="(option, index) of filteredOptions"
						:id="id + '-' + index"
						:key="index"
						:data-header="option.header && !headerInOption ? option.header : null"
						class="multiselect__element"
						:class="{
							'last-child': index + 1 === filteredOptions.length,
							'border-bottom': option.bottomBorder
						}"
						:role="!(option && (option.$isLabel || option.$isDisabled)) ? 'option' : null"
					>
						<span
							v-if="!(option && (option.$isLabel || option.$isDisabled))"
							:class="optionHighlight(index, option)"
							:data-select="option && option.isTag ? tagPlaceholder : selectLabelText"
							:data-selected="selectedLabelText"
							:data-deselect="deselectLabelText"
							class="multiselect__option"
							:data-qa-multiselect-option="getOptionLabel(option)"
							@click.stop="select(option)"
							@mouseenter.self="pointerSet(index)"
						>
							<slot name="option" :option="option" :search="search" :index="index">
								<span>{{ getOptionLabel(option) }}</span>
							</slot>
							<Icon
								v-if="isSelected(option) && showOptionSelector"
								name="i-tick"
								state="dark"
								:effects="false"
								size="small"
							/>
						</span>
						<span
							v-if="option && (option.$isLabel || option.$isDisabled)"
							:data-select="groupSelect && selectGroupLabelText"
							:data-deselect="groupSelect && deselectGroupLabelText"
							:class="groupHighlight(index, option)"
							class="multiselect__option"
							@mouseenter.self="groupSelect && pointerSet(index)"
							@mousedown.prevent="selectGroup(option)"
						>
							<slot name="option" :option="option" :search="search" :index="index">
								<span>{{ getOptionLabel(option) }}</span>
							</slot>
							<Icon
								v-if="isSelected(option) && showOptionSelector"
								name="i-tick"
								state="dark"
								:effects="false"
								size="small"
							/>
						</span>
					</li>
				</template>
				<li v-show="showNoResults && filteredOptions.length === 0 && search && !loading">
					<span
						class="multiselect__option text-wrap paragraph-1"
						data-qa-multiselect-empty-search
					>
						<slot name="noResult" :search="search">
							No elements found. Consider changing the search query.
						</slot>
					</span>
				</li>
				<li v-show="showNoOptions && options.length === 0 && !search && !loading">
					<span class="multiselect__option" data-qa-multiselect-empty>
						<slot name="noOptions">List is empty.</slot>
					</span>
				</li>
				<slot name="afterList" />
			</ul>
		</div>
	</transition>
</div>
